/**
 * /preview's words, shared by PreviewPage.tsx and the server (index.ts), the
 * way convertPairs.ts is for the converter pages: the title, description and
 * FAQ a crawler reads before any JS runs, and the FAQPage JSON-LD, are the
 * same strings the page renders, so they can't drift apart.
 *
 * The FAQ's claims are checked against the code, and have to stay that way:
 * the formats are READ_FORMATS in app/utils/embroidery.py, and "What happens
 * to my uploaded file?" describes /api/convert -- the file goes to the
 * server, its conversions are kept content-addressed with nothing expiring
 * them, and /preview sends no token, so no filename or account is attached.
 */

export const PREVIEW_PATH = "/preview";

export const PREVIEW_TITLE = "Free Embroidery File Viewer – Open DST, PES, JEF & EXP Files Online";

export const PREVIEW_DESCRIPTION =
  "View DST, PES, JEF, EXP and VP3 embroidery files online. See your design and its dimensions for free, with no software or account required.";

export const PREVIEW_HEADING = "Free Embroidery File Viewer";

export const PREVIEW_INTRO =
  "View an embroidery design and check its dimensions without installing any software. Just upload your file to get started.";

export interface PreviewFaq {
  question: string;
  // One string per paragraph.
  answer: string[];
}

export const PREVIEW_FAQ: PreviewFaq[] = [
  {
    question: "How do I open a DST file online?",
    answer: [
      "Upload your DST file and click View Design. You'll get a preview of the embroidery along with its dimensions. DST files don't include thread colours, so the viewer uses placeholder colours instead.",
    ],
  },
  {
    question: "How can I view a PES file without installing software?",
    answer: [
      "Upload your PES file and we'll generate a preview in your browser. It works on computers, phones and tablets, with no software to install.",
    ],
  },
  {
    question: "Can I open JEF or EXP files online?",
    answer: [
      "Yes. The viewer supports JEF, EXP and VP3, along with DST and PES. Upload your file to see the design and its dimensions.",
    ],
  },
  {
    question: "Can I open an EMB file?",
    answer: [
      "Not yet. EMB is Wilcom's native design format and generally requires compatible software to open. If you can get a DST or PES version of the design, you can view it here.",
    ],
  },
  {
    question: "Is the viewer free? Do I need an account?",
    answer: ["It's free to use, and you don't need an account. You can view as many files as you like."],
  },
  {
    question: "What happens to my uploaded file?",
    answer: [
      "Your file is uploaded to our server so we can generate the preview. We keep the file and any converted versions so you can return to the preview or use the conversion and stitch-viewing links later.",
      "You don't need to sign in, and we don't store the original filename. Files are accessed through links that aren't easy to guess, so anyone you share a link with may be able to view that design.",
    ],
  },
  {
    question: "Can I convert my file after viewing it?",
    answer: [
      "Yes. Click Convert to another format below the preview, and the converter will open with your file already loaded.",
    ],
  },
];

export const PREVIEW_STEPS = [
  "Drop your file in the box above, or click to select it.",
  "Click View Design to see the embroidery preview and its width and height in inches.",
  "To convert the file, click the link below the preview. Your file will already be loaded in the converter.",
];

// Only formats READ_FORMATS opens.
export const PREVIEW_FORMATS: { name: string; line: string }[] = [
  { name: "DST", line: "Commonly used by commercial embroidery machines. Doesn't store thread colours." },
  { name: "PES", line: "Used by Brother and Baby Lock machines." },
  { name: "JEF", line: "Used by Janome machines." },
  { name: "EXP", line: "Used by Melco and supported by Bernina machines. Doesn't store thread colours." },
  { name: "VP3", line: "Used by Husqvarna Viking and Pfaff machines." },
];

export const PREVIEW_COLOUR_NOTE =
  "DST and EXP files don't contain thread colour information, so the viewer uses placeholder colours to distinguish different sections of the design. The stitch pattern and dimensions are preserved.";

export const PREVIEW_STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Embroidery File Viewer",
    url: "https://embroiderytext.com/preview",
    description: PREVIEW_DESCRIPTION,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: PREVIEW_FAQ.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      // The visible paragraphs, joined: the same words in the same order.
      acceptedAnswer: { "@type": "Answer", text: faq.answer.join(" ") },
    })),
  },
];

/** The /preview.md mirror for crawlers that don't run JS. */
export function previewMarkdown(): string {
  return `# ${PREVIEW_HEADING}

${PREVIEW_INTRO}

## How to view an embroidery file online

${PREVIEW_STEPS.map((step, i) => `${i + 1}. ${step}`).join("\n")}

## Supported embroidery file formats

${PREVIEW_FORMATS.map((f) => `- **${f.name}:** ${f.line}`).join("\n")}

${PREVIEW_COLOUR_NOTE}

## Frequently asked questions

${PREVIEW_FAQ.map((faq) => `### ${faq.question}\n\n${faq.answer.join("\n\n")}`).join("\n\n")}
`;
}
